/**
 * Hook for managing product (SPU/SKU) data and operations.
 */

import { useState, useEffect, useCallback } from 'react'
import { api, ApiRequestError } from '@/lib/api'

export interface SKU {
  id: number
  product_id: number
  name: string
  price: number
  currency: string
  attributes: Record<string, string>
  inventory: number | null
  status: string
  created_at: string
  updated_at: string
}

export interface Product {
  id: number
  name: string
  description: string
  type: 'one_time' | 'subscription'
  status: string
  metadata: Record<string, unknown>
  created_at: string
  updated_at: string
  skus: SKU[]
}

export interface CreateProductData {
  name: string
  description?: string
  type: 'one_time' | 'subscription'
  metadata?: Record<string, unknown>
}

export interface UpdateProductData {
  name?: string
  description?: string
  status?: string
  metadata?: Record<string, unknown>
}

export interface CreateSKUData {
  name: string
  price: number
  currency?: string
  attributes?: Record<string, string>
  inventory?: number | null
}

export interface UpdateSKUData {
  name?: string
  price?: number
  currency?: string
  attributes?: Record<string, string>
  inventory?: number | null
  status?: string
}

interface UseProductsReturn {
  products: Product[]
  loading: boolean
  error: string | null
  refresh: () => Promise<void>
  createProduct: (data: CreateProductData) => Promise<Product>
  updateProduct: (id: number, data: UpdateProductData) => Promise<Product>
  deleteProduct: (id: number) => Promise<void>
  createSKU: (productId: number, data: CreateSKUData) => Promise<SKU>
  updateSKU: (productId: number, skuId: number, data: UpdateSKUData) => Promise<SKU>
  deleteSKU: (productId: number, skuId: number) => Promise<void>
}

export function useProducts(): UseProductsReturn {
  const [products, setProducts] = useState<Product[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await api.get<Product[]>('/products')
      setProducts(response.data)
    } catch (err) {
      const message = err instanceof ApiRequestError ? err.message : 'Failed to fetch products'
      setError(message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    refresh()
  }, [refresh])

  const createProduct = async (data: CreateProductData): Promise<Product> => {
    const response = await api.post<Product>('/products', data)
    await refresh()
    return response.data
  }

  const updateProduct = async (id: number, data: UpdateProductData): Promise<Product> => {
    const response = await api.put<Product>(`/products/${id}`, data)
    await refresh()
    return response.data
  }

  const deleteProduct = async (id: number): Promise<void> => {
    await api.delete(`/products/${id}`)
    await refresh()
  }

  const createSKU = async (productId: number, data: CreateSKUData): Promise<SKU> => {
    const response = await api.post<SKU>(`/products/${productId}/skus`, data)
    await refresh()
    return response.data
  }

  const updateSKU = async (productId: number, skuId: number, data: UpdateSKUData): Promise<SKU> => {
    const response = await api.put<SKU>(`/products/${productId}/skus/${skuId}`, data)
    await refresh()
    return response.data
  }

  const deleteSKU = async (productId: number, skuId: number): Promise<void> => {
    await api.delete(`/products/${productId}/skus/${skuId}`)
    await refresh()
  }

  return {
    products,
    loading,
    error,
    refresh,
    createProduct,
    updateProduct,
    deleteProduct,
    createSKU,
    updateSKU,
    deleteSKU,
  }
}
